import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import useFlexBoxStyles from 'common/flexBox';
import { useAbortController } from 'common/hooks';
import { useSettingValue } from 'common/hooks/useSettingValue';
import LayoutForm from 'common/layouts/LayoutForm';
import { uploadFileMultipartSignedUrl } from 'common/utils/file';
import { useFormFields } from 'libs/hooksLib';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getAssets, resetAssets, setAssets } from 'redux/slices/assetSlice';
import { cancelCloseCustomDialogAction, closeCustomDialogAction } from 'redux/slices/customDialogSlice';
import { getOrganization, getProvider } from 'redux/slices/organizationSlice';
import { getShortVideoCreatePending } from 'redux/slices/shortVideoSlice';
import { makeStyles } from 'tss-react/mui';
import ShortVIdeoFormConfig from './ShortVideoFormData';
import ShortVIdeoFormRecording from './ShortVideoFormRecording';
import { Typography } from '@mui/material';

const useStyles = makeStyles()((theme) => {
    return {
        title: {
          padding: theme.spacing(3, 3, 0, 3),  
        },
        customWidth: {
            maxHeight: 'none',
            width: '50vw',
            [theme.breakpoints.up('xl')]: {
                width: '60vw',
            },
            [theme.breakpoints.down('mg')]: {
                width: '65vw',
            },
            [theme.breakpoints.down('ms')]: {
                width: '50vw',
            },
        },
    };
});

const ShortVideoForm = ({ data, onSubmit, asset, onCancel }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();
    const { enqueueSnackbar } = useSnackbar();
    const cancelConfirmTitle = t('user.userInviteForm.cancelConfirm');
    const cancelConfirmMessage = t('account.accountProfileForm.cancelMessage');
    const [openCancelDlg, setOpenCancelDlg] = useState(false);
    const assets = useSelector(getAssets);
    const [step, setStep] = useState(0);
    const [file, setFile] = useState(null);
    const [url, setUrl] = useState();
    const savePending = useSelector(getShortVideoCreatePending);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [subForm, setSubForm] = useState(null);
    const organization = useSelector(getOrganization);
    const instantSearchEnable = useSettingValue(SettingsKey.INSTANT_SEARCH);
    const provider = useSelector(getProvider);
    const instantSearchEnabled = Boolean(
        organization.provider !== 'shopify-private' && instantSearchEnable && provider?.searchEngineId,
    );
    const getSignal = useAbortController();

    useEffect(() => {
        if (asset) {
            dispatch(setAssets([asset]));
        }
    }, [asset, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(resetAssets());
        };
    }, [data, dispatch]);

    const cancelDlg = () => {
        setOpenCancelDlg(false);
        dispatch(cancelCloseCustomDialogAction());
    };

    const closeSubForm = () => {
        setSubForm(null);
    };

    const handleSetSteep = (s) => {
        closeSubForm();
        setStep(s);
    };

    const finalCancel = () => {
        restart();
        if (onCancel) {
            try {
                onCancel();
            } catch (error) {
                console.error('Error calling short video form onCancel callback:', error);
            }
        }
        dispatch(closeCustomDialogAction());
    };

    const manageCancel = () => {
        setOpenCancelDlg(true);
    };

    const initialForm = useMemo(
        () => ({
            name: data.name,
            description: data.description,
        }),
        [data],
    );

    const [fields, handleFieldChange, resetForm] = useFormFields(initialForm);

    const restart = useCallback(() => {
        setStep();
        resetForm(initialForm);
    }, [initialForm, resetForm]);

    const onSuccess = useCallback(
        ({ task, data } = {}) => {
            restart();
        },
        [restart],
    );

    const onFailure = useCallback(
        (error) => {
            enqueueSnackbar(t('app.common.error'), { variant: 'error' });
        },
        [enqueueSnackbar, t],
    );

    const filterAssets = useCallback(
        (assets) => {
            let filteredAssets = [];
            assets.forEach((asset) => {
                if (filteredAssets.indexOf(asset.id) === -1) {
                    if (
                        (!instantSearchEnabled && asset.providerType !== 'shopify-private') ||
                        (instantSearchEnabled && asset.providerType !== 'shopify-private' && asset.type !== 'product')
                    ) {
                        filteredAssets.push(asset.id);
                    }
                }
            });
            return filteredAssets.filter((a) => a);
        },
        [instantSearchEnabled],
    );

    const getProducts = useCallback(
        (assets) => {
            if (instantSearchEnabled) {
                return assets.filter((asset) => asset.type === 'product').map((a) => a.variantGroupId);
            } else {
                return assets.filter((asset) => asset.type === 'product').map((a) => a.id || a.externalId);
            }
        },
        [instantSearchEnabled],
    );

    const handleSubmit = useCallback(
        (url) => {
            onSubmit(
                {
                    name: fields.name,
                    description: fields.description,
                    url: url,
                    assets: filterAssets(assets),
                    products: getProducts(assets),
                    format: 'video/webm',
                    size: file.size,
                    duration: 0,
                },
                onSuccess,
                onFailure,
            );
        },
        [assets, fields, file, filterAssets, getProducts, onFailure, onSubmit, onSuccess],
    );

    const onUploadFileSignedUrl = useCallback(
        ({ file, onInit, onProgress, onComplete, bucketType, signal, path }) => {
            return uploadFileMultipartSignedUrl(
                file,
                {
                    size: 1024 * 1024 * 100, // 256MB
                    path: path || `${organization.id}/assets/multimedia`,
                    onInit: () => {
                        onInit();
                    },
                    onProgress: (loaded, total) => {
                        onProgress(Math.min(100, Math.round((loaded / total) * 100)));
                    },
                    onComplete: (url) => {
                        onComplete(url);
                    },
                    bucketType: bucketType,
                },
                signal,
            );
        },
        [organization.id],
    );

    const handleUploadFile = useCallback(async () => {
        if (url) {
            handleSubmit(url);
        } else if (validateFile(file)) {
            await onUploadFileSignedUrl({
                file,
                onInit: () => {
                    setUploading(true);
                },
                onProgress: (progress) => {
                    setProgress(progress);
                },
                onComplete: (url) => {
                    setUploading(false);
                    setUrl(url);
                    handleSubmit(url);
                },
                signal: getSignal(),
                bucketType: 'resources',
            });
        }
    }, [file, handleSubmit, url, getSignal, onUploadFileSignedUrl]);

    const validateFile = (file) => {
        if (!file) {
            return false;
        }
        return file.size / 1048576 <= 512;
    };

    const handleSubmitVideo = (videoFile) => {
        setStep(1);
        setFile(videoFile);
    };
    
    const title = t('shortVideo.form.title').toUpperCase();

    return (
        <LayoutForm
            title={step ? title : (
                <Typography fontWeight="bold" variant="onliveH5" className={classes.title}>
                    {title}
                </Typography>
            )}
            noPadding={!step}
            customClasses={classes.customWidth}
            size="inherit"
            confirmDlg={{
                open: openCancelDlg,
                accept: finalCancel,
                cancel: cancelDlg,
                titleDlg: cancelConfirmTitle,
                messageDlg: cancelConfirmMessage,
            }}
        >
            {subForm || null}
            <div
                className={cx(
                    flexBox.gap2,
                    flexBox.flex,
                    flexBox.column,
                    flexBox.overflowHidden,
                    subForm && flexBox.displayNone,
                )}
            >
                {step === 0 ? (
                    <ShortVIdeoFormRecording
                        onSubmit={handleSubmitVideo}
                        step={step}
                        uploading={uploading}
                        progress={progress}
                    />
                ) : (
                    <ShortVIdeoFormConfig
                        step={step}
                        setStep={handleSetSteep}
                        openSubForm={setSubForm}
                        closeSubForm={closeSubForm}
                        fields={fields}
                        assets={assets}
                        pending={uploading || savePending}
                        cancel={manageCancel}
                        handleSubmit={handleUploadFile}
                        handleFieldChange={handleFieldChange}
                    />
                )}
            </div>
        </LayoutForm>
    );
};

export default ShortVideoForm;
